import React, { useEffect } from 'react';
import { IStore } from "../../redux/reducers";
import { useDispatch, useSelector } from "react-redux";
import { LOGGED_OUT_MESSAGE, SUBSCRIPTION_EXPIRED_MESSAGE } from '../../strings';
import { Auth as Authorization } from 'aws-amplify';
import * as AppActions from '../../redux/actions/appAction';
import * as UserActions from '../../redux/actions/userAction';
import * as LoginActions from '../../redux/actions/loginAction';
import { getDeviceId, isElectron } from 'src/utils/miscUtils';
import * as NotificationAction from '../../redux/actions/notificationAction';
import CustomAlertDialog from '../CustomAlertDialog';
import { getText } from 'src/utils/i18n';

export default function HomeStatusBar() {

    const deviceId = useSelector((store: IStore) => store.notification.deviceId);

    const MINUTE_MS = 5 * 60 * 1000;

    const [dialogTitle, setDialogTitle] = React.useState("");
    const [dialogMessage, setDialogMessage] = React.useState("");

    const [visible, setVisible] = React.useState(false);
    const showDialog = () => setVisible(true);
    const hideDialog = () => setVisible(false);

    const dispatch = useDispatch();

    const showForceLogoutPopup = useSelector((store: IStore) => store.app.showForceLogoutPopup);
    const subscriptionStatus = useSelector((store: IStore) => store.user.subscriptionStatus);
    const userLoggedIn = useSelector((store: IStore) => store.login.userLoggedIn);
    const isOnline = useSelector((store: IStore) => store.app.isOnline);
    const cloudAuth = useSelector((store: IStore) => store.app.cloudAuth);
    const effectiveOnline =
        isOnline !== false
        && (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean' || navigator.onLine !== false);

    useEffect(() => {
        setDeviceId();
        // Delay the first fetch to allow the session to stabilize after login
        const timer = setTimeout(() => {
            fetchUserDetails();
        }, 3000); // 3 second delay

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (isElectron() && showForceLogoutPopup) {
            // Desktop offline-first: never force-logout. Clear the popup state and require re-auth for sync.
            dispatch(AppActions.forceLogoutHandled());
            dispatch(AppActions.setCloudAuthStatus({
                status: 'invalid',
                reason: 'auth_check_failed',
                updatedAt: Date.now(),
            }));
            return;
        }
        if (!isElectron() && showForceLogoutPopup && effectiveOnline) {
            setDialogTitle(getText('loggedOut'));
            setDialogMessage(LOGGED_OUT_MESSAGE)
            showDialog();
        }
    }, [showForceLogoutPopup, effectiveOnline]);

    useEffect(() => {
        if (subscriptionStatus !== "ACTIVE") {
            setDialogTitle(getText('subscriptionExpired'));
            setDialogMessage(SUBSCRIPTION_EXPIRED_MESSAGE)
            showDialog();
        }
    }, [subscriptionStatus]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchUserDetails()
        }, MINUTE_MS);

        return () => clearInterval(interval);
    }, [userLoggedIn, effectiveOnline, cloudAuth?.status])

    async function setDeviceId() {
        var currentDeviceId = deviceId;
        if (currentDeviceId === null || currentDeviceId === undefined) {
            currentDeviceId = await getDeviceId();
            dispatch(NotificationAction.setDeviceId(currentDeviceId));
        }
    }

    async function fetchUserDetails() {
        try {
            // Only proceed if user is actually logged in
            if (userLoggedIn !== "LOGGED_IN") {
                console.log('User not logged in, skipping fetch');
                return;
            }
            // Never force-logout while offline. Token/userinfo checks can fail when offline.
            if (!effectiveOnline) {
                console.log('Offline, skipping auth/userinfo fetch');
                return;
            }

            if (isElectron()) {
                // Desktop offline-first: don't police auth via Cognito; rely on API responses to mark cloudAuth invalid.
                if (cloudAuth?.status === 'invalid') {
                    return;
                }
                await fetchUserProfile();
                return;
            }

            const user = await Authorization.currentUserInfo();
            if (!user || !user.username) {
                console.log('User info is invalid, triggering force logout');
                // Double check we're still logged in before forcing logout
                if (userLoggedIn === "LOGGED_IN") {
                    dispatch(AppActions.forceLogout());
                }
                return;
            }
            await fetchUserProfile();
        } catch (e) {
            console.log('fetch user profile error occurred', e)
            // Only trigger force logout if user is still supposed to be logged in
            if (userLoggedIn === "LOGGED_IN" && effectiveOnline) {
                if (isElectron()) {
                    dispatch(AppActions.setCloudAuthStatus({
                        status: 'invalid',
                        reason: 'auth_check_failed',
                        updatedAt: Date.now(),
                    }));
                } else {
                    dispatch(AppActions.forceLogout());
                }
            }
        }
    }

    async function fetchUserProfile() {
        dispatch(UserActions.getUserProfile.request());
    }

    return (
        <CustomAlertDialog
            title={dialogTitle}
            description={dialogMessage}
            cancelText={getText('cancel')}
            confirmText={getText('continue')}
            cancelDisabled={true}
            isOpen={visible}
            onCancelClicked={hideDialog}
            onConfirmClicked={async () => {
                hideDialog()
                dispatch(AppActions.forceLogoutHandled());
                await Authorization.signOut()
                dispatch(AppActions.logout());
            }}
        />
    );
};
